import React, { useState, useEffect } from 'react';
import { CheckCircle, Package } from 'lucide-react';

interface ModuleOption {
  _id?: string;
  id?: string;
  name: string;
}

interface ModuleSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  modules: ModuleOption[];
  selectedModules: string[];
  onConfirm: (selected: string[]) => void;
}

const ModuleSelectionModal: React.FC<ModuleSelectionModalProps> = ({
  isOpen,
  onClose,
  modules,
  selectedModules,
  onConfirm
}) => {
  const [selected, setSelected] = useState<string[]>(selectedModules);

  useEffect(() => {
    setSelected(selectedModules);
  }, [selectedModules]);

  if (!isOpen) return null;

  const handleToggle = (moduleId: string) => {
    if (selected.includes(moduleId)) {
      setSelected(selected.filter(id => id !== moduleId));
    } else {
      setSelected([...selected, moduleId]);
    }
  };

  const handleSelectAll = () => {
    if (selected.length === modules.length) {
      setSelected([]);
    } else {
      setSelected(modules.map(mod => mod._id || mod.id || ''));
    }
  };

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  const progress = modules.length > 0 ? Math.round((selected.length / modules.length) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 rounded-t-2xl">
          <div className="flex items-center gap-3 text-white">
            <Package className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Modules</h2>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-8">
          {/* Select All */}
          <div className="mb-6 pb-4 border-b-2 border-gray-200">
            <label className="flex items-center gap-3 p-4 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={selected.length === modules.length && modules.length > 0}
                onChange={handleSelectAll}
                className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-lg font-bold text-blue-600">Select All</span>
            </label>
          </div>

          {/* Module Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {modules.map((mod) => {
              const moduleId = mod._id || mod.id || '';
              const isSelected = selected.includes(moduleId);

              return (
                <label
                  key={moduleId}
                  className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                    isSelected
                      ? 'bg-green-50 border-green-400 shadow-md'
                      : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggle(moduleId)}
                    className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className={`text-base font-semibold flex-1 ${isSelected ? 'text-green-700' : 'text-gray-700'}`}>
                    {mod.name}
                  </span>
                  {isSelected && <CheckCircle className="w-6 h-6 text-green-600" />}
                </label>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">
                Progress: {selected.length} of {modules.length} completed
              </span>
              <span className="text-sm font-bold text-blue-600">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-500 to-green-500 h-full transition-all duration-300 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected.length === 0}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleSelectionModal;
